import { messageFactory } from '@/store/messaging';
import { actionCreator, selectors } from '.';
import { handleSagaError } from '@/store/common';
import { put, select, takeLatest } from 'redux-saga/effects';
import { IRegion, IRegionCampaign, IRegionCampaignUpdate } from '@/models';
import { api } from '@/api/controllers/api';

export function* saga() {
	yield takeLatest(
		actionCreator.LOAD_REGION_CAMPAIGNS_START,
		onLoadRegionCampaignsStart
	);
	yield takeLatest(
		actionCreator.LOAD_ALL_REGION_CAMPAIGNS_START,
		onLoadAllRegionCampaignsStart
	);
	yield takeLatest(
		actionCreator.UPDATE_CAMPAIGN_START,
		onUpdateCampaignStart
	);
	yield takeLatest(actionCreator.UPDATE_REGIONS_START, onUpdateRegionsStart);
}

function* onLoadRegionCampaignsStart() {
	try {
		const region: IRegion = yield select(selectors.region.get);
		yield put(
			messageFactory.progress(
				`Loading Configuration for ${region.company}/${region.businessUnit}/${region.serviceRegion}...`
			)
		);

		const response: IRegionCampaign =
			yield api.regionCampaigns.getRegionCampaigns(region);

		yield put(actionCreator.LOAD_REGION_CAMPAIGNS_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [
			actionCreator.LOAD_REGION_CAMPAIGNS_FAILED()
		]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onLoadAllRegionCampaignsStart() {
	try {
		yield put(messageFactory.progress('Loading Region Campaigns...'));
		const response: IRegionCampaign[] =
			yield api.regionCampaigns.getAllRegionRegionCampaigns();
		yield put(actionCreator.LOAD_ALL_REGION_CAMPAIGNS_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [
			actionCreator.LOAD_ALL_REGION_CAMPAIGNS_FAILED()
		]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onUpdateCampaignStart() {
	try {
		const request: IRegionCampaignUpdate = yield select(
			selectors.regionCampaignUpdate.getData
		);
		const region: IRegion = yield select(selectors.region.get);
		yield api.regionCampaigns.postRegionCampaigns([request]);

		const response: IRegionCampaign =
			yield api.regionCampaigns.getRegionCampaigns(region);

		const regionCampaigns: IRegionCampaign[] = yield select(
			selectors.regionCampaigns.getData
		);

		if (regionCampaigns.length > 0) {
			const regionCampaignsResponse: IRegionCampaign[] =
				yield api.regionCampaigns.getAllRegionRegionCampaigns();

			yield put(
				actionCreator.UPDATE_CAMPAIGN_COMPLETED({
					regionCampaign: response,
					regionCampaigns: regionCampaignsResponse
				})
			);
		} else {
			yield put(
				actionCreator.UPDATE_CAMPAIGN_COMPLETED({
					regionCampaign: response
				})
			);
		}
	} catch (error) {
		yield handleSagaError(error, [actionCreator.UPDATE_CAMPAIGN_FAILED()]);
	}
}

function* onUpdateRegionsStart() {
	try {
		yield put(
			messageFactory.progress('Saving Campaign Region Settings...')
		);
		const updates: IRegionCampaignUpdate[] = yield select(
			selectors.regionCampaignUpdates.getData
		);
		yield api.regionCampaigns.postRegionCampaigns(updates);

		const response: IRegionCampaign[] =
			yield api.regionCampaigns.getAllRegionRegionCampaigns();

		const regionCampaign: IRegionCampaign = yield select(
			selectors.regionCampaign.getData
		);

		if (
			regionCampaign &&
			regionCampaign.company !== '' &&
			regionCampaign.businessUnit !== '' &&
			regionCampaign.serviceRegion !== ''
		) {
			const regionCampaignResponse: IRegionCampaign =
				yield api.regionCampaigns.getRegionCampaigns({
					company: regionCampaign.company,
					businessUnit: regionCampaign.businessUnit,
					serviceRegion: regionCampaign.serviceRegion
				});

			yield put(
				actionCreator.UPDATE_REGIONS_COMPLETED({
					regionCampaigns: response,
					regionCampaign: regionCampaignResponse
				})
			);
		} else {
			yield put(
				actionCreator.UPDATE_REGIONS_COMPLETED({
					regionCampaigns: response
				})
			);
		}
	} catch (error) {
		yield handleSagaError(error, [actionCreator.UPDATE_REGIONS_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}
